import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtDateTime } from "@/lib/date";
import { MessageSquare, Send, Loader2, Users, Radio, BookOpen, CalendarClock, Phone, Copy, Music2, HandHeart, Inbox, Image as ImageIcon, Download, Share2, Wand2, Trash2, Plus } from "lucide-react";

export default function SmsManager() {
  const [status, setStatus] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [testNum, setTestNum] = useState("");
  const [kw, setKw] = useState("");
  const [savingKw, setSavingKw] = useState(false);
  const [scheduled, setScheduled] = useState([]);
  const [scheduleOn, setScheduleOn] = useState(false);
  const [sendAt, setSendAt] = useState("");
  const [repeat, setRepeat] = useState("none");
  const [kwImg, setKwImg] = useState("");
  const [kwBusy, setKwBusy] = useState(false);
  const [kwTheme, setKwTheme] = useState("navy");
  const [pushing, setPushing] = useState(false);
  const [replyText, setReplyText] = useState({});
  const [replyBusy, setReplyBusy] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [newTpl, setNewTpl] = useState("");
  const [suggestBusy, setSuggestBusy] = useState(null);
  const [showTpl, setShowTpl] = useState(false);

  const markRead = (phone) => {
    api.post(`/sms/threads/${encodeURIComponent(phone)}/read`).then(() => {
      setMessages((ms) => ms.map((m) => (m.from === phone && m.direction !== "out" ? { ...m, read: true } : m)));
    }).catch(() => {});
  };

  const suggestReply = async (t) => {
    const lastIn = [...t.items].reverse().find((m) => m.direction !== "out");
    if (!lastIn) { toast.error("Nothing to reply to"); return; }
    setSuggestBusy(t.phone);
    try {
      const { data } = await api.post("/sms/suggest-reply", { message: lastIn.body, name: t.name });
      setReplyText((r) => ({ ...r, [t.phone]: data.suggestion }));
      toast.success("Draft ready — edit and send");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not draft a reply"); }
    finally { setSuggestBusy(null); }
  };

  const addTemplate = async () => {
    const text = newTpl.trim(); if (!text) return;
    try { const { data } = await api.post("/sms/templates", { text }); setTemplates((xs) => [...xs, data]); setNewTpl(""); toast.success("Template added"); }
    catch (e) { toast.error("Could not add template"); }
  };
  const delTemplate = async (id) => {
    try { await api.delete(`/sms/templates/${id}`); setTemplates((xs) => xs.filter((x) => x.id !== id)); } catch (e) {}
  };

  const sendReply = async (phone) => {
    const text = (replyText[phone] || "").trim();
    if (!text) return;
    setReplyBusy(phone);
    try {
      await api.post("/sms/reply", { phone, message: text });
      setReplyText((r) => ({ ...r, [phone]: "" }));
      api.get("/sms/messages").then((r) => setMessages(r.data.messages || [])).catch(() => {});
      toast.success("Reply sent");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send reply"); }
    finally { setReplyBusy(null); }
  };

  const load = () => {
    api.get("/sms/status").then((r) => { setStatus(r.data); setKw((r.data.settings?.join_keywords || []).join(", ")); }).catch(() => {});
    api.get("/sms/messages").then((r) => setMessages(r.data.messages || [])).catch(() => {});
    api.get("/sms/scheduled").then((r) => setScheduled(r.data.scheduled || [])).catch(() => {});
    api.get("/sms/templates").then((r) => setTemplates(r.data.templates || [])).catch(() => {});
  };
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, []);

  const toggle = async (key) => {
    const next = { ...status.settings, [key]: !status.settings[key] };
    setStatus({ ...status, settings: next });
    try { await api.post("/sms/settings", { [key]: next[key] }); }
    catch { toast.error("Could not update"); load(); }
  };

  const sendBlast = async () => {
    if (!msg.trim()) { toast.error("Write a message first"); return; }
    if (scheduleOn && !sendAt) { toast.error("Pick a date & time"); return; }
    setSending(true);
    try {
      const payload = { message: msg };
      if (scheduleOn && sendAt) { payload.send_at = new Date(sendAt).toISOString(); payload.repeat = repeat; }
      const r = await api.post("/sms/blast", payload);
      if (r.data.scheduled) {
        const rep = r.data.repeat && r.data.repeat !== "none" ? ` (repeats ${r.data.repeat})` : "";
        toast.success(`Scheduled for ${fmtDateTime(r.data.send_at)}${rep}`);
        setSendAt(""); setScheduleOn(false); setRepeat("none"); load();
      } else {
        toast.success(`Texting ${r.data.recipients} opted-in listener${r.data.recipients === 1 ? "" : "s"}…`);
      }
      setMsg("");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send blast"); }
    finally { setSending(false); }
  };

  const cancelScheduled = async (id) => {
    try { await api.delete(`/sms/scheduled/${id}`); setScheduled((x) => x.filter((s) => s.id !== id)); toast.success("Scheduled text canceled"); }
    catch { toast.error("Could not cancel"); }
  };

  const makeKeywordImage = async () => {
    setKwBusy(true);
    try {
      const r = await api.post("/sms/keyword-image", { theme: kwTheme });
      setKwImg(r.data.image);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create graphic"); }
    finally { setKwBusy(false); }
  };

  const downloadKwImage = () => {
    const a = document.createElement("a");
    a.href = kwImg; a.download = "iihr-text-keyword.png";
    document.body.appendChild(a); a.click(); a.remove();
  };

  const pushToSocial = async () => {
    setPushing(true);
    try {
      await api.post("/sms/keyword-image/to-social", { theme: kwTheme });
      toast.success("Added to your Social queue — find it in the Social tab");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not push to social"); }
    finally { setPushing(false); }
  };

  const savePromoAuto = async (patch) => {
    const cur = status?.settings?.promo_auto || { enabled: false, cadence: "weekly", theme: kwTheme };
    const next = { ...cur, theme: kwTheme, ...patch };
    setStatus((st2) => ({ ...st2, settings: { ...st2.settings, promo_auto: next } }));
    try {
      const r = await api.post("/sms/settings", { promo_auto: next });
      setStatus((st2) => ({ ...st2, settings: r.data }));
      if (patch.enabled === true) toast.success(`Auto-posting the keyword promo ${next.cadence}`);
      else if (patch.enabled === false) toast.success("Auto-post turned off");
    } catch (e) { toast.error("Could not update"); load(); }
  };

  const sendTest = async () => {
    try {
      const r = await api.post("/sms/test", { to: testNum || undefined, message: msg || undefined });
      toast.success(`Test sent to ${r.data.to}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send test"); }
  };

  const saveKeywords = async () => {
    setSavingKw(true);
    try {
      const r = await api.post("/sms/settings", { join_keywords: kw });
      setStatus((s) => ({ ...s, settings: r.data }));
      setKw((r.data.join_keywords || []).join(", "));
      toast.success("Opt-in keyword saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save keyword"); }
    finally { setSavingKw(false); }
  };

  const kindBadge = (k) => {
    if (k === "song") return <span className="inline-flex items-center gap-1 text-[#5B9BFF]"><Music2 className="w-3.5 h-3.5" /> Song</span>;
    if (k === "prayer") return <span className="inline-flex items-center gap-1 text-[#E6B34A]"><HandHeart className="w-3.5 h-3.5" /> Prayer</span>;
    if (k === "join") return <span className="inline-flex items-center gap-1 text-emerald-400"><Users className="w-3.5 h-3.5" /> Joined</span>;
    return <span className="inline-flex items-center gap-1 text-zinc-400"><MessageSquare className="w-3.5 h-3.5" /> Message</span>;
  };

  const s = status || {};
  const st = s.settings || {};
  const threads = (() => {
    const map = new Map();
    for (const m of messages) {
      if (!map.has(m.from)) map.set(m.from, { phone: m.from, name: m.name, items: [] });
      const t = map.get(m.from);
      t.items.push(m);
      if (m.name && !t.name) t.name = m.name;
    }
    const arr = [...map.values()];
    for (const t of arr) {
      t.unread = t.items.filter((m) => m.direction !== "out" && !m.read).length;
      t.items.reverse(); // oldest -> newest
    }
    return arr; // already ordered by most-recent sender (messages are desc)
  })();
  const totalUnread = threads.reduce((n, t) => n + t.unread, 0);

  return (
    <div className="space-y-6" data-testid="sms-manager">
      <div className="flex items-center gap-2">
        <MessageSquare className="w-6 h-6 text-[#E6B34A]" />
        <h2 className="font-display font-black text-2xl tracking-tight">Texting (SMS)</h2>
      </div>

      {/* Status row */}
      <div className="grid sm:grid-cols-3 gap-3">
        <div className="p-4 rounded-2xl bg-[#141C42] border border-white/5">
          <p className="overline text-zinc-400 flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> Opted-in</p>
          <p className="text-2xl font-black mt-1" data-testid="sms-recipients">{s.recipients ?? "—"}</p>
        </div>
        <div className="p-4 rounded-2xl bg-[#141C42] border border-white/5">
          <p className="overline text-zinc-400 flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> Your number</p>
          <p className="text-sm font-semibold mt-2 truncate">{s.from_number || "Not set"}</p>
        </div>
        <div className="p-4 rounded-2xl bg-[#141C42] border border-white/5">
          <p className="overline text-zinc-400 flex items-center gap-1.5"><Inbox className="w-3.5 h-3.5" /> Inbound texts</p>
          <p className="text-2xl font-black mt-1">{s.inbound_count ?? 0}</p>
        </div>
      </div>

      {!s.configured && (
        <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-200 text-sm">
          Twilio isn't fully configured yet — check your Account SID, Auth Token, and number.
        </div>
      )}

      {/* Blast composer */}
      <div className="p-5 rounded-2xl bg-[#141C42] border border-white/5">
        <h3 className="font-display font-bold text-lg flex items-center gap-2"><Send className="w-5 h-5 text-[#E6B34A]" /> Send an SMS blast</h3>
        <p className="text-sm text-zinc-400 mt-1 mb-3">Goes to every opted-in listener. "Reply STOP to opt out" is added automatically.</p>
        <textarea value={msg} onChange={(e) => setMsg(e.target.value)} maxLength={800} rows={4} data-testid="sms-blast-message"
          placeholder="e.g. Join us tonight at 7 for a live praise & worship hour! Tune in at inhisimageradio.com"
          className="w-full rounded-xl bg-[#1E2A5A] border border-white/10 px-3.5 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#E6B34A] resize-none" />
        <div className="flex items-center justify-between mt-1">
          <span className="text-xs text-zinc-500">{msg.length}/800</span>
        </div>
        <div className="flex items-center gap-3 mt-3 flex-wrap">
          <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer select-none">
            <input type="checkbox" checked={scheduleOn} onChange={(e) => setScheduleOn(e.target.checked)} data-testid="sms-schedule-toggle" className="w-4 h-4 accent-[#E6B34A]" />
            Schedule for later
          </label>
          {scheduleOn && (
            <input type="datetime-local" value={sendAt} onChange={(e) => setSendAt(e.target.value)} data-testid="sms-schedule-at"
              className="h-10 rounded-lg bg-[#1E2A5A] border border-white/10 px-3 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#E6B34A]" />
          )}
          {scheduleOn && (
            <select value={repeat} onChange={(e) => setRepeat(e.target.value)} data-testid="sms-repeat"
              className="h-10 rounded-lg bg-[#1E2A5A] border border-white/10 px-3 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#E6B34A]">
              <option value="none">Doesn't repeat</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          )}
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          <button onClick={sendBlast} disabled={sending || !msg.trim()} data-testid="sms-send-blast" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#E6B34A] text-[#0A1030] font-bold hover:bg-[#CF9A34] transition-colors disabled:opacity-50">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : (scheduleOn ? <CalendarClock className="w-4 h-4" /> : <Send className="w-4 h-4" />)} {scheduleOn ? "Schedule blast" : `Send to ${s.recipients ?? 0}`}
          </button>
          <div className="flex items-center gap-2">
            <input value={testNum} onChange={(e) => setTestNum(e.target.value)} placeholder="Test to (555) 123-4567" data-testid="sms-test-number"
              className="h-11 rounded-xl bg-[#1E2A5A] border border-white/10 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#E6B34A]" />
            <button onClick={sendTest} data-testid="sms-send-test" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/5 text-zinc-200 font-semibold hover:bg-white/10 transition-colors">Send test</button>
          </div>
        </div>
        {scheduled.length > 0 && (
          <div className="mt-4 space-y-2" data-testid="sms-scheduled-list">
            <p className="overline text-zinc-400">Scheduled ({scheduled.length})</p>
            {scheduled.map((sc) => (
              <div key={sc.id} data-testid={`sms-scheduled-${sc.id}`} className="flex items-start gap-3 p-3 rounded-xl bg-[#101735] border border-white/5">
                <CalendarClock className="w-4 h-4 text-[#E6B34A] shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-[#E6B34A] font-semibold flex items-center gap-2">
                    {fmtDateTime(sc.send_at)}
                    {sc.repeat && sc.repeat !== "none" && <span className="px-1.5 py-0.5 rounded bg-[#5B9BFF]/20 text-[#5B9BFF] text-[10px] font-bold uppercase">{sc.repeat}</span>}
                  </p>
                  <p className="text-sm text-zinc-300 line-clamp-2">{sc.message}</p>
                </div>
                <button onClick={() => cancelScheduled(sc.id)} data-testid={`sms-cancel-${sc.id}`} className="shrink-0 text-xs text-zinc-400 hover:text-rose-300 font-semibold">Cancel</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Automations */}
      <div className="p-5 rounded-2xl bg-[#141C42] border border-white/5">
        <h3 className="font-display font-bold text-lg flex items-center gap-2"><CalendarClock className="w-5 h-5 text-[#E6B34A]" /> Automated texts</h3>
        <div className="mt-3 space-y-2">
          {[
            { key: "daily_devotion", icon: <BookOpen className="w-4 h-4 text-[#E6B34A]" />, title: "Daily devotion by text", desc: "Text opted-in listeners the devotion each morning." },
            { key: "reminders", icon: <Radio className="w-4 h-4 text-[#E6B34A]" />, title: "Show & event reminders", desc: "Text listeners before shows and events they follow." },
          ].map((row) => (
            <button key={row.key} onClick={() => toggle(row.key)} data-testid={`sms-toggle-${row.key}`} className="w-full flex items-center gap-3 p-3.5 rounded-xl bg-[#101735] border border-white/5 hover:border-[#E6B34A]/30 transition-colors text-left">
              <div className={`shrink-0 w-11 h-6 rounded-full p-0.5 transition-colors ${st[row.key] ? "bg-[#E6B34A]" : "bg-white/15"}`}>
                <div className={`w-5 h-5 rounded-full bg-white transition-transform ${st[row.key] ? "translate-x-5" : "translate-x-0"}`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold flex items-center gap-2">{row.icon} {row.title}</p>
                <p className="text-xs text-zinc-400 mt-0.5">{row.desc}</p>
              </div>
              <span className={`text-xs font-bold ${st[row.key] ? "text-[#E6B34A]" : "text-zinc-500"}`}>{st[row.key] ? "On" : "Off"}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Custom opt-in keyword */}
      <div className="p-5 rounded-2xl bg-[#141C42] border border-white/5" data-testid="sms-keyword-card">
        <h3 className="font-display font-bold text-lg flex items-center gap-2"><MessageSquare className="w-5 h-5 text-[#E6B34A]" /> Opt-in keyword</h3>
        <p className="text-sm text-zinc-400 mt-1 mb-3">Words listeners can text to join. Promote one on air (e.g. "Text <b>GOSPEL</b> to {s.from_number || "your number"}"). Separate multiple with commas.</p>
        <div className="flex flex-wrap gap-2 items-center">
          <input value={kw} onChange={(e) => setKw(e.target.value)} data-testid="sms-keyword-input" placeholder="JOIN, PRAISE, GOSPEL"
            className="flex-1 min-w-[220px] h-11 rounded-xl bg-[#1E2A5A] border border-white/10 px-3.5 text-sm uppercase tracking-wide focus:outline-none focus:ring-2 focus:ring-[#E6B34A]" />
          <button onClick={saveKeywords} disabled={savingKw} data-testid="sms-keyword-save" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#E6B34A] text-[#0A1030] font-bold hover:bg-[#CF9A34] transition-colors disabled:opacity-50">
            {savingKw ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
          </button>
        </div>
        <div className="flex flex-wrap gap-1.5 mt-3">
          {(st.join_keywords || []).map((k) => (
            <span key={k} className="px-2.5 py-1 rounded-full bg-[#E6B34A]/15 text-[#E6B34A] text-xs font-bold">{k}</span>
          ))}
        </div>

        <div className="mt-5 pt-5 border-t border-white/10">
          <p className="text-sm font-semibold flex items-center gap-2"><ImageIcon className="w-4 h-4 text-[#E6B34A]" /> On-air graphic</p>
          <p className="text-xs text-zinc-400 mt-0.5 mb-3">A branded "Text {(st.join_keywords || ["JOIN"])[0]} to {s.from_number}" image to flash during broadcasts or post on social.</p>
          <div className="flex flex-wrap items-center gap-2">
            {["navy", "gold", "sunrise"].map((t) => (
              <button key={t} onClick={() => setKwTheme(t)} data-testid={`kw-theme-${t}`} className={`w-8 h-8 rounded-full border-2 transition-transform ${kwTheme === t ? "border-white scale-110" : "border-white/20"}`}
                style={{ background: t === "navy" ? "linear-gradient(135deg,#0C1338,#1A2452)" : t === "gold" ? "linear-gradient(135deg,#E9B850,#C9942E)" : "linear-gradient(135deg,#301446,#C44E3E)" }} title={t} />
            ))}
            <button onClick={makeKeywordImage} disabled={kwBusy} data-testid="kw-generate" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#E6B34A] text-[#0A1030] font-bold hover:bg-[#CF9A34] transition-colors disabled:opacity-50">
              {kwBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />} Create graphic
            </button>
          </div>
          {kwImg && (
            <div className="mt-4 flex flex-col items-start gap-3">
              <img src={kwImg} alt="On-air keyword graphic" data-testid="kw-image" className="w-64 h-64 rounded-xl border border-white/10" />
              <div className="flex flex-wrap gap-2">
                <button onClick={downloadKwImage} data-testid="kw-download" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 text-zinc-200 font-semibold hover:bg-white/10 transition-colors"><Download className="w-4 h-4" /> Download</button>
                <button onClick={pushToSocial} disabled={pushing} data-testid="kw-push-social" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#E6B34A] text-[#0A1030] font-bold hover:bg-[#CF9A34] transition-colors disabled:opacity-50">{pushing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />} Push to social</button>
              </div>
            </div>
          )}

          <div className="mt-4 flex items-center gap-3 p-3.5 rounded-xl bg-[#101735] border border-white/5" data-testid="promo-auto-row">
            <button onClick={() => savePromoAuto({ enabled: !(st.promo_auto?.enabled) })} data-testid="promo-auto-toggle" className={`shrink-0 w-11 h-6 rounded-full p-0.5 transition-colors ${st.promo_auto?.enabled ? "bg-[#E6B34A]" : "bg-white/15"}`}>
              <div className={`w-5 h-5 rounded-full bg-white transition-transform ${st.promo_auto?.enabled ? "translate-x-5" : "translate-x-0"}`} />
            </button>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold">Auto-post promo to social</p>
              <p className="text-xs text-zinc-400 mt-0.5">Automatically queue this keyword graphic to your Social tab on a schedule.</p>
            </div>
            <select value={st.promo_auto?.cadence || "weekly"} onChange={(e) => savePromoAuto({ cadence: e.target.value })} data-testid="promo-auto-cadence"
              className="shrink-0 h-9 rounded-lg bg-[#1E2A5A] border border-white/10 px-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#E6B34A]">
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>
        </div>
      </div>

      {/* Two-way inbox */}
      <div className="p-5 rounded-2xl bg-[#141C42] border border-white/5">
        <h3 className="font-display font-bold text-lg flex items-center gap-2"><Inbox className="w-5 h-5 text-[#E6B34A]" /> Inbound texts
          {totalUnread > 0 && <span data-testid="sms-unread-total" className="ml-1 inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-[#FF3B30] text-white text-[11px] font-bold">{totalUnread}</span>}
        </h3>
        <p className="text-sm text-zinc-400 mt-1 mb-1">Listeners can text <b>{(st.join_keywords || ["JOIN"])[0]}</b> to opt in, <b>SONG &lt;title&gt;</b> for a request, or <b>PRAY &lt;need&gt;</b> for prayer. Song & prayer texts also show in those tabs.</p>
        <div className="flex items-start gap-2 p-3 rounded-lg bg-[#101735] border border-white/5 text-xs text-zinc-400 mb-4">
          <span className="shrink-0">Twilio webhook URL:</span>
          <code className="text-[#5B9BFF] break-all flex-1">{s.webhook_url}</code>
          <button onClick={() => { navigator.clipboard.writeText(s.webhook_url || ""); toast.success("Webhook URL copied"); }} data-testid="sms-copy-webhook" className="shrink-0 text-zinc-400 hover:text-white"><Copy className="w-3.5 h-3.5" /></button>
        </div>
        {messages.length === 0 ? (
          <p className="text-sm text-zinc-500">No inbound texts yet.</p>
        ) : (
          <div className="space-y-3" data-testid="sms-inbox">
            {threads.map((t) => (
              <div key={t.phone} className={`rounded-xl bg-[#101735] p-3.5 ${t.unread > 0 ? "border border-[#E6B34A]/50" : "border border-white/5"}`} data-testid={`sms-thread-${t.phone}`}>
                <p className="text-xs font-bold text-zinc-300 flex items-center gap-1.5 mb-2"><Phone className="w-3.5 h-3.5 text-[#E6B34A]" /> {t.name || t.phone}{t.name && <span className="text-zinc-500 font-normal">· {t.phone}</span>}
                  {t.unread > 0 && <span data-testid={`sms-unread-${t.phone}`} className="ml-auto inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-[#FF3B30] text-white text-[10px] font-bold">{t.unread} new</span>}
                </p>
                <div className="space-y-1.5 mb-2.5 max-h-56 overflow-y-auto">
                  {t.items.map((m) => (
                    <div key={m.id} className={`flex ${m.direction === "out" ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[80%] rounded-2xl px-3 py-1.5 ${m.direction === "out" ? "bg-[#E6B34A] text-[#0A1030]" : "bg-[#1E2A5A] text-zinc-100"}`}>
                        {m.direction !== "out" && <p className="text-[10px] opacity-70 mb-0.5">{kindBadge(m.kind)}</p>}
                        <p className="text-sm whitespace-pre-wrap break-words">{m.body}</p>
                        <p className="text-[10px] opacity-60 mt-0.5">{m.direction === "out" ? "You" : "Them"} · {fmtDateTime(m.created_at)}</p>
                      </div>
                    </div>
                  ))}
                </div>
                {templates.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-2" data-testid={`sms-templates-${t.phone}`}>
                    {templates.map((tpl) => (
                      <button key={tpl.id} onClick={() => setReplyText((r) => ({ ...r, [t.phone]: tpl.text }))} data-testid={`sms-template-chip-${tpl.id}`}
                        className="px-2.5 py-1 rounded-full bg-[#1E2A5A] text-zinc-300 text-[11px] hover:bg-[#E6B34A] hover:text-[#0A1030] transition-colors max-w-[200px] truncate" title={tpl.text}>{tpl.text}</button>
                    ))}
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <input value={replyText[t.phone] || ""} onChange={(e) => setReplyText((r) => ({ ...r, [t.phone]: e.target.value }))}
                    onFocus={() => { if (t.unread > 0) markRead(t.phone); }}
                    onKeyDown={(e) => { if (e.key === "Enter") sendReply(t.phone); }}
                    placeholder="Type a reply…" data-testid={`sms-reply-input-${t.phone}`}
                    className="flex-1 min-w-0 rounded-lg bg-[#1E2A5A] border border-white/10 px-3 py-2 text-sm text-zinc-200 placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-[#E6B34A]" />
                  <button onClick={() => suggestReply(t)} disabled={suggestBusy === t.phone} data-testid={`sms-suggest-${t.phone}`} title="AI suggested reply"
                    className="shrink-0 inline-flex items-center gap-1.5 px-2.5 py-2 rounded-lg bg-[#1E2A5A] text-[#E6B34A] text-xs font-bold hover:bg-[#263569] transition-colors disabled:opacity-50">
                    {suggestBusy === t.phone ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                  </button>
                  <button onClick={() => sendReply(t.phone)} disabled={replyBusy === t.phone || !(replyText[t.phone] || "").trim()} data-testid={`sms-reply-send-${t.phone}`}
                    className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#E6B34A] text-[#0A1030] text-xs font-bold hover:bg-[#CF9A34] transition-colors disabled:opacity-50">
                    {replyBusy === t.phone ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Reply
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-5 pt-4 border-t border-white/5" data-testid="sms-templates-manage">
          <button onClick={() => setShowTpl((v) => !v)} className="text-xs font-bold text-[#E6B34A] hover:text-[#CF9A34] transition-colors flex items-center gap-1.5">
            <MessageSquare className="w-3.5 h-3.5" /> {showTpl ? "Hide" : "Manage"} quick replies ({templates.length})
          </button>
          {showTpl && (
            <div className="mt-3 space-y-2">
              {templates.map((tpl) => (
                <div key={tpl.id} className="flex items-center gap-2 text-sm text-zinc-300 bg-[#101735] border border-white/5 rounded-lg px-3 py-1.5" data-testid={`sms-template-row-${tpl.id}`}>
                  <span className="flex-1 min-w-0 truncate">{tpl.text}</span>
                  <button onClick={() => delTemplate(tpl.id)} data-testid={`sms-template-del-${tpl.id}`} className="shrink-0 text-zinc-500 hover:text-[#FF3B30]"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
              <div className="flex items-center gap-2">
                <input value={newTpl} onChange={(e) => setNewTpl(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") addTemplate(); }}
                  placeholder="Add a quick reply…" data-testid="sms-template-new"
                  className="flex-1 min-w-0 rounded-lg bg-[#1E2A5A] border border-white/10 px-3 py-2 text-sm text-zinc-200 placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-[#E6B34A]" />
                <button onClick={addTemplate} disabled={!newTpl.trim()} data-testid="sms-template-add" className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#E6B34A] text-[#0A1030] text-xs font-bold hover:bg-[#CF9A34] transition-colors disabled:opacity-50"><Plus className="w-3.5 h-3.5" /> Add</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
